import { observable, action, computed, toJS } from "mobx";
import moment from "moment";
import {
  searchTooltip,
  cubeLinkVariables,
  referenceFramework,
  nonReferenceFramework,
} from "../constants/constants-container";

const IS_BIRD_APP = process.env.REACT_APP_ENV === "bird";

// const TYPE_FRAMEWORK = 'Framework'
const TYPE_CUBE = "Cube";
const TYPE_VARIABLE = "Variable";
const TYPE_MEMBER = "Member";
const TYPE_SUBDOMAIN = "Subdomain";
const TYPE_VARIABLESET = "VariableSet";
const TYPE_DOMAIN = "Domain";
const TYPE_MEMBERHIERARCHY = "MemberHierarchy";
// const TYPE_LEGALTEXT = "LegalText";
// const TYPE_COMBINATION = 'Combination'

export const SEARCH_AUTOCOMPLETE_START_CHAR_COUNT = 3;

export default class SearchStore {
  constructor(http, userStore) {
    this.http = http;
    this.userStore = userStore;
    this.init();
  }

  // supported types in desired order
  objectTypes = [
    // TYPE_FRAMEWORK,
    TYPE_VARIABLE,
    TYPE_MEMBER,
    TYPE_CUBE,
    TYPE_DOMAIN,
    TYPE_SUBDOMAIN,
    TYPE_VARIABLESET,
    TYPE_MEMBERHIERARCHY,
    // TYPE_LEGALTEXT,
    //,TYPE_COMBINATION
  ];

  @observable
  cubes = [];

  @observable
  showFilters = false;

  @observable
  selectedTypes = [TYPE_VARIABLE, TYPE_MEMBER, TYPE_CUBE];

  @observable
  searchQuery = "";

  @observable
  lastSearchQuery = "";

  @observable
  onlyValidObjects = true;

  @observable
  perPage = 20;

  @observable
  currentPage = 1;

  @observable
  searchResults = observable.map();

  @observable
  autoCompleteResults = observable.map();

  @observable
  exactSearchPerPage = 10;

  @observable
  exactSearchCurrentPage = 1;

  @observable
  exactSearchResults = observable.map();

  @observable
  searchMemberExpression = "";

  @observable
  frameworks = [];

  @observable
  metadata = {};

  @observable
  searchMetadata = {};

  @observable
  SDDBusinessContent = undefined;

  @observable
  selectedFramework = undefined;

  @observable
  allMaintenanceAgencies = [];

  @observable
  cubeTypeOptions = [
    {
      value: "C",
      label: "Collection",
    },
    {
      value: "D",
      label: "Dissemination",
    },
    {
      value: "EIL",
      label: "Enriched Input Layer",
    },
    {
      value: "FL",
      label: "Feedback Loop",
    },
    {
      value: "IL",
      label: "Input Layer",
    },
    {
      value: "NRC",
      label: "Non-Reference Cube",
    },
    {
      value: "RC",
      label: "Reference Cube",
    },
    {
      value: "S",
      label: "Staging",
    },
    {
      value: "D_AS",
      label: "Dissemination Analytical System",
    },
    {
      value: "D_DISC",
      label: "Dissemination DISC",
    },
  ];

  // when cube types for a profile are changes, reflect the same change in the backend (view precaching, etc..)
  // TODO: use profiles/cube types coming from backend
  @observable
  userProfiles = [
    {
      id: "business",
      label: "Business User",
      cubeTypes: ["D", "RC", "NRC", "D_AS", "D_DISC"],
    },
    {
      id: "power",
      label: "Power User",
      cubeTypes: ["C", "D", "RC", "NRC"],
    },
    {
      id: "full",
      label: "Full Profile",
      cubeTypes: [],
    },
    {
      id: "custom",
      label: "Custom Configuration",
      cubeTypes: [],
    },
  ];

  @observable
  selectedUserProfile = this.userProfiles[0];

  @observable
  selectedCubeTypes = ["D", "RC", "NRC", "D_AS", "D_DISC"];

  @observable
  userProfileChanged = false;

  @observable
  selectedDate = undefined;

  @observable
  searchFrameworkExpression = undefined;

  @observable
  searchFramework = undefined;

  @observable
  macroFrameworksMaintenanceAgencies = ["ECB4", "IMF"];

  @action
  init = async () => {
    let targetUserProfile;
    const upId = localStorage.getItem("user-profile");
    if (upId) {
      targetUserProfile = this.userProfiles.find(
        (profile) => profile.id === upId
      );
    } else {
      const actorObject = await this.http.h_service_fetch_user_data();
      const isActorFromEscb =
        actorObject &&
        actorObject.actor &&
        actorObject.actor.authSystem === "ESCB";

      targetUserProfile = isActorFromEscb
        ? this.userProfiles[2]
        : this.userProfiles[0];
      this.selectedCubeTypes = targetUserProfile.cubeTypes;
    }
    this.selectedUserProfile = targetUserProfile;

    let targetCubeTypes;
    const cts = localStorage.getItem("cube-types");
    if (cts) {
      targetCubeTypes = JSON.parse(cts);
    } else {
      targetCubeTypes = this.selectedCubeTypes;
    }
    this.selectedCubeTypes = IS_BIRD_APP ? [] : targetCubeTypes;

    if (IS_BIRD_APP) {
      localStorage.setItem("user-profile", "full");
      localStorage.setItem("cube-types", "[]");
    }
    this.fetchAllMaintenanceAgencies();
  };

  @action
  toggleFilters = () => {
    this.showFilters = !this.showFilters;
  };

  @action
  toggleFilter = (type) => {
    if (this.selectedTypes.findIndex((value) => value === type) === -1) {
      this.selectedTypes.push(type);
    } else {
      this.selectedTypes = this.selectedTypes.filter((value) => {
        return value !== type;
      });
    }
  };

  @action
  setSelectedTypes = (types) => {
    this.selectedTypes = types;
  };

  @action
  clearFilters = () => {
    this.selectedTypes = [];
  };

  @action
  onSearchQueryChange = (event) => {
    this.setSearchQuery(event.target.value);
    this.setLastSearchQuery(event.target.value);
    if (
      !event?.target?.value ||
      event.target.value.length < SEARCH_AUTOCOMPLETE_START_CHAR_COUNT
    ) {
      this.autoCompleteResults = observable.map();
    }
  };

  @action
  setSearchQuery = (query) => {
    this.searchQuery = query ? query : "";
  };

  @action
  setLastSearchQuery = (query) => {
    this.lastSearchQuery = query ? query : "";
  };

  @action
  toggleOnlyValidObjects = () => {
    this.onlyValidObjects = !this.onlyValidObjects;
  };

  @action
  setPage = (page) => {
    this.currentPage = page;
  };

  @action
  setSearchResults = (searchResults) => {
    this.searchResults = searchResults;
  };

  @action
  setPerPage = (perPage) => {
    this.perPage = perPage;
  };

  @action
  setExactSearchCurrentPage = (exactSearchCurrentPage) => {
    this.exactSearchCurrentPage = exactSearchCurrentPage;
  };

  @action
  setExactSearchPerPage = (exactSearchPerPage) => {
    this.exactSearchPerPage = exactSearchPerPage;
  };

  @action
  setSearchMemberExpression = (type, searchMemberExpression) => {
    this.searchMemberExpression = searchMemberExpression;
  };

  @action
  setMetadata = (metadata) => {
    this.metadata = metadata;
  };

  @action
  setSearchMetadata = (searchMetadata) => {
    this.searchMetadata = searchMetadata;
  };

  @action
  fetchSearchResults = () => {
    let selectedFrameworks;
    if (this.selectedFramework) {
      if (this.selectedFramework.type === "Framework") {
        selectedFrameworks = [this.selectedFramework.tid];
      }
      if (this.selectedFramework.type === "FrameworkGroup") {
        selectedFrameworks = [...this.selectedFramework.groupTids];
      }
    } else {
      selectedFrameworks = [];
    }

    if (!this.searchQuery) return;

    this.searchResults = {};

    const searchItems = [];
    const typesForSearch = this.selectedTypes.length
      ? this.selectedTypes
      : this.objectTypes;
    // const containsConditionValues = this.searchQuery.split(" "); // multi-values // TODO escaping?
    const searchValue = this.searchQuery; // everything processed on backend (splitting, escaping, stopwords)
    // sort to predefined order
    const sortedTypes = [];
    this.objectTypes.forEach((type) => {
      if (typesForSearch.includes(type)) sortedTypes.push(type);
    });
    sortedTypes.forEach((type) => {
      if (this.searchMetadata && this.searchMetadata[type]) {
        const containsConditionFields =
          this.searchMetadata[type].searchableFields;
        // valid on - only for historizable types, by default current date if not filled
        const validOn = this.searchMetadata[type].historizable
          ? this.selectedDate
            ? moment(this.selectedDate).format("YYYY-MM-DD")
            : moment().format("YYYY-MM-DD")
          : null;
        const item = {
          type: type,
          containsConditionFields: containsConditionFields,
          // containsConditionValues: containsConditionValues,
          searchValue: searchValue,
          validOn: validOn,
          frameworkTids: selectedFrameworks,
          cubeTypes:
            this.selectedCubeTypes.length > 0 ? this.selectedCubeTypes : [],
          // : this.cubeTypeOptions.map((cto) => cto.value),
        };
        searchItems.push(item);
      }
    });
    const fetchProps = {
      searchItems: searchItems,
      maxResults: this.perPage,
      firstResult: (this.currentPage - 1) * this.perPage,
    };

    this.http.fetchSearchResults(fetchProps).then((response) => {
      this.searchResults = response;
    });
  };

  @action
  fetchSearchAutocompleteResults = () => {
    let selectedFrameworks;
    if (this.selectedFramework) {
      if (this.selectedFramework.type === "Framework") {
        selectedFrameworks = [this.selectedFramework.tid];
      }
      if (this.selectedFramework.type === "FrameworkGroup") {
        selectedFrameworks = [...this.selectedFramework.groupTids];
      }
    } else {
      selectedFrameworks = [];
    }

    if (!this.searchQuery) return;

    // this.searchResults = {};

    const searchItems = [];
    const typesForSearch = this.selectedTypes.length
      ? this.selectedTypes
      : this.objectTypes;
    // const containsConditionValues = this.searchQuery.split(" "); // multi-values // TODO escaping?
    const searchValue = this.searchQuery; // everything processed on backend (splitting, escaping, stopwords)
    // sort to predefined order
    const sortedTypes = [];
    this.objectTypes.forEach((type) => {
      if (typesForSearch.includes(type)) sortedTypes.push(type);
    });
    sortedTypes.forEach((type) => {
      if (this.searchMetadata && this.searchMetadata[type]) {
        const containsConditionFields = this.searchMetadata[
          type
        ].searchableFields.filter(
          (field) =>
            field === "name" || field === "code" || field === "datasetUrl"
        );

        // valid on - only for historizable types, by default current date if not filled
        const validOn = this.searchMetadata[type].historizable
          ? this.selectedDate
            ? moment(this.selectedDate).format("YYYY-MM-DD")
            : moment().format("YYYY-MM-DD")
          : null;
        const item = {
          type: type,
          containsConditionFields: containsConditionFields,
          searchValue: searchValue,
          validOn: validOn,
          frameworkTids: selectedFrameworks,
          cubeTypes:
            this.selectedCubeTypes.length > 0 ? this.selectedCubeTypes : [],
        };
        searchItems.push(item);
      }
    });
    const fetchProps = {
      searchItems: searchItems,
      maxResults: this.perPage,
      firstResult: (this.currentPage - 1) * this.perPage,
      usedForAutocompletion: true,
    };

    this.http.fetchSearchResults(fetchProps).then((response) => {
      this.autoCompleteResults = response;
    });
  };

  @action
  findSelectedFrameworks = () => {
    let selectedFrameworks;

    if (this.selectedFramework) {
      if (this.selectedFramework) {
        if (this.selectedFramework.type === "Framework") {
          selectedFrameworks = [this.selectedFramework.tid];
        }
        if (this.selectedFramework.type === "FrameworkGroup") {
          selectedFrameworks = [...this.selectedFramework.groupTids];
        }
      } else {
        selectedFrameworks = [];
      }
    }
    return selectedFrameworks;
  };

  @action
  fetchExactMatchSearchLookup = (item) => {
    let universalFilter = null;
    if (this.searchMemberExpression && this.searchMemberExpression !== "") {
      universalFilter = {
        filterType: "STRING_FIELD",
        consideredFields: ["name", "code"],
        filter: {
          contains: this.searchMemberExpression,
        },
      };
    }

    let lookupMembershipFilter = null;
    if (this.selectedFramework) {
      let selectedFrameworks;
      if (this.selectedFramework) {
        if (this.selectedFramework.type === "Framework") {
          selectedFrameworks = [this.selectedFramework.tid];
        }
        if (this.selectedFramework.type === "FrameworkGroup") {
          selectedFrameworks = [...this.selectedFramework.groupTids];
        }
      } else {
        selectedFrameworks = [];
      }

      lookupMembershipFilter = {
        framworkTids: selectedFrameworks,
      };
    }

    const params = {
      lookupDefinitionKey: "Search Exact Match - Variable",
      type: item.type,
      tids: [item.tid],
      validOn: new Date(),
      resultsInTree: false,
      resultsInBuckets: true,
      queryParamsForBuckets: {
        Members: {
          filter: {
            type: "Member",
            universalFilter: universalFilter,
            lookupMembershipFilter: lookupMembershipFilter,
          },
          limit: {
            offset: (this.exactSearchCurrentPage - 1) * this.exactSearchPerPage,
            limit: this.exactSearchPerPage,
          },
        },
      },
      cubeTypes: this.selectedCubeTypes,
    };

    this.http.fetchExactMatchSearchLookup(params).then((response) => {
      this.exactSearchResults = response;
    });
  };

  @action
  fetchExactMatchSearchLookupState = (item, state, framework) => {
    let universalFilter = null;
    if (state.searchMemberExpression && state.searchMemberExpression !== "") {
      universalFilter = {
        filterType: "STRING_FIELD",
        consideredFields: ["name", "code"],
        filter: {
          contains: state.searchMemberExpression,
        },
      };
    }

    let lookupMembershipFilter = null;
    if (this.selectedFramework) {
      let selectedFrameworks;
      if (this.selectedFramework) {
        if (this.selectedFramework.type === "Framework") {
          selectedFrameworks = [this.selectedFramework.tid];
        }
        if (this.selectedFramework.type === "FrameworkGroup") {
          selectedFrameworks = [...this.selectedFramework.groupTids];
        }
      } else {
        selectedFrameworks = [];
      }

      lookupMembershipFilter = {
        framworkTids: selectedFrameworks,
      };
    }
    if (framework) {
      lookupMembershipFilter = {
        framworkTids: framework.tid ? [framework.tid] : null,
      };
    }

    const params = {
      lookupDefinitionKey: "Search Exact Match - Variable",
      type: item.type,
      tids: [item.tid],
      validOn: new Date(),
      resultsInTree: false,
      resultsInBuckets: true,
      queryParamsForBuckets: {
        Members: {
          filter: {
            type: "Member",
            universalFilter: universalFilter,
            lookupMembershipFilter: lookupMembershipFilter,
          },
          limit: {
            offset:
              (state.exactSearchCurrentPage - 1) * state.exactSearchPerPage,
            limit: state.exactSearchPerPage,
          },
        },
      },
      cubeTypes: this.selectedCubeTypes,
    };
    return this.http.fetchExactMatchSearchLookup(params).then((response) => {
      return response;
    });
  };

  @action
  fetchSDDBusinessContent = () => {
    const params1 = {
      tableName: "Cube",
      filter: {
        type: "Cube",
        bid: { in: [1] },
        sort: [],
        frameworkId: { in: ["SDD"] },
      },
      pageSize: 1000,
      queryOffset: 0,
      hidden: true,
    };
    const params2 = {
      tableName: "Member",
      filter: {
        type: "Member",
        bid: { in: [1] },
        sort: [],
        domainId: { in: ["TYP_RL"] },
      },
      pageSize: 1000,
      queryOffset: 0,
      hidden: true,
    };
    const params3 = {
      tableName: "Variable",
      filter: {
        type: "Variable",
        sort: [],
        bid: { in: [1] },
        variableId: { in: ["PRIMARY_CONCEPT"] },
      },
      pageSize: 1000,
      queryOffset: 0,
      hidden: true,
    };
    Promise.all([
      this.http.fetchContentManagementTableData(params1),
      this.http.fetchContentManagementTableData(params2),
      this.http.fetchContentManagementTableData(params3),
    ]).then((response) => {
      if (!response || !response[0] || !response[1] || !response[2]) return;
      this.SDDBusinessContent = [
        ...response[0].results.reduce((acc, curr) => acc.concat(curr), []),
        ...response[1].results.reduce((acc, curr) => acc.concat(curr), []),
        ...response[2].results.reduce((acc, curr) => acc.concat(curr), []),
      ];
    });
  };

  @action
  fetchMetadata = async () => {
    return this.http.fetchMetadata().then((results) => {
      this.setMetadata(results);
      return results;
    });
  };

  @action
  fetchSearchMetadata = async () => {
    return this.http.fetchSearchMetadata().then((results) => {
      this.setSearchMetadata(results);
      return results;
    });
  };

  @action
  fetchFrameworks = async () => {
    // TODO Matyi:
    // only one initial call to queryfw to obtain frameworks with cube types and counts
    // then only compute to filter frameworks shown in Framework component of Home Page and framework selector in Search
    // refresh app on change of profile? - should be computed automatically
    // consume SSE ProjectPromoted in EventSourceStore - refetch frameworks
    // this.http.service_fetch_frameworks().then((response) => {
    return this.http
      .service_fetch_frameworks_with_cube_type_counts()
      .then((response) => {
        if (!response || !response.results) return;
        this.frameworks = response.results
          // filter to have only PUBLISHED frameworks as they have some content in production branch
          .filter((framework) => framework.frameworkStatus === "PUBLISHED");
      });
  };

  @action
  fetchCubes = () => {
    const cubeFilter = {
      type: "Cube",
      filter: {
        sort: [
          {
            column: "name",
            direction: "ASC",
          },
        ],
        bid: {
          in: [1, 0],
        },
        type: "Cube",
      },
    };
    this.http.service_fetch_filteredEntities(cubeFilter, (response) => {
      if (response && response.results) {
        this.cubes = response.results
          .slice()
          .sort((a, b) => {
            return a.code === b.code
              ? moment(a.validFrom).isBefore(moment(b.validFrom))
                ? 1
                : -1
              : a.code < b.code;
          })
          .filter(
            (item, index, self) =>
              index === self.findIndex((t) => t.code === item.code)
          )
          .map((cube) => {
            return {
              cubeId: cube.cubeId,
              type: cube.type,
              tid: cube.tid,
              name: cube.name,
              code: cube.code,
              isPublished: cube.published,
              maintenanceAgencyId: cube.maintenanceAgencyId,
              frameworkId: cube.frameworkId,
              description: cube.description,
            };
          });
      } else {
        this.cubes = [];
      }
    });
  };

  @action
  setSelectedFramework = (framework) => {
    this.selectedFramework = framework;
  };

  @action
  setSearchFramework = (framework) => {
    this.searchFramework = framework;
  };

  @action
  setSelectedCubeTypes = (cubeTypes) => {
    this.selectedCubeTypes = cubeTypes;
    localStorage.setItem("cube-types", JSON.stringify(cubeTypes));
  };

  @action
  setSelectedDate = (date) => {
    this.selectedDate = new Date(date);
  };

  @action
  setSelectedUserProfile = (profile) => {
    this.selectedUserProfile = profile;
    const selectedUserProfile = IS_BIRD_APP ? "full" : profile.id;
    localStorage.setItem("user-profile", selectedUserProfile);
  };

  @action
  setUserProfileChanged = (value) => {
    this.userProfileChanged = value;
  };

  @action
  setSearchFrameworkExpression = (value) => {
    this.searchFrameworkExpression = value;
  };

  @computed
  get getSearchResults() {
    return toJS(this.searchResults);
  }

  @computed
  get getExactSearchResults() {
    return toJS(this.exactSearchResults);
  }

  @computed
  get getCubes() {
    return this.cubes;
  }

  @computed
  get getPublishedCubes() {
    return this.cubes.filter((cube) => cube.isPublished);
  }

  @computed
  get getUnpublishedCubes() {
    return this.cubes.filter((cube) => !cube.isPublished);
  }

  @computed
  get getFrameworks() {
    return [...this.frameworks].filter((framework) => {
      if (!framework.cubeCountsByType) return false;

      if (
        this.getSelectedCubeTypes.length === 0 &&
        this.selectedUserProfile.id === "full"
      ) {
        return true;
      }
      if (IS_BIRD_APP) return true;
      return Object.keys(framework.cubeCountsByType).some(
        (key) =>
          this.getSelectedCubeTypes.includes(key) &&
          framework.cubeCountsByType[key] > 0
      );
    });
  }

  @computed
  get getMicroFrameworks() {
    const frameworks = this.getFrameworks;
    if (!frameworks) return [];
    return [...frameworks].filter((framework) =>
      ["ANCRDT", "RIAD", "SHS_REF", "CSDB"].includes(framework.code)
    );
  }

  @computed
  get getMacroFrameworks() {
    const frameworks = this.getFrameworks;
    if (!frameworks) return [];
    return [...frameworks].filter((framework) =>
      this.macroFrameworksMaintenanceAgencies.includes(
        framework.maintenanceAgencyId
      )
    );
  }

  @computed
  get getEbaFrameworks() {
    const frameworks = this.getFrameworks;
    if (!frameworks) return [];
    return [...frameworks].filter((framework) =>
      ["COREP", "FINREP", "RES", "AE"].includes(framework.code)
    );
  }

  @computed
  get getPagesCount() {
    return Math.ceil(this.searchResults.totalResults / this.perPage);
  }

  @computed
  get getExactSearchPagesCount() {
    return Math.ceil(
      this.exactSearchResults.totalResults / this.exactSearchPerPage
    );
  }

  @computed
  get filtersVisible() {
    return this.showFilters === true;
  }

  @computed
  get getObjectTypes() {
    return this.objectTypes;
  }

  @computed
  get getSelectedTypes() {
    return this.selectedTypes;
  }

  @computed
  get getMetadata() {
    return this.metadata;
  }

  @computed
  get getSearchMetadata() {
    return this.searchMetadata;
  }

  @computed
  get getCurrentPage() {
    return this.currentPage;
  }

  @computed
  get getPerPage() {
    return this.perPage;
  }

  @computed
  get getExactSearchCurrentPage() {
    return this.exactSearchCurrentPage;
  }

  @computed
  get getExactSearchPerPage() {
    return this.exactSearchPerPage;
  }

  @computed
  get getSearchMemberExpression() {
    return this.searchMemberExpression;
  }
  @computed
  get getSearchQuery() {
    return this.searchQuery;
  }

  @computed
  get getLastSearchQuery() {
    return this.lastSearchQuery;
  }

  @computed
  get getTotalResults() {
    return this.searchResults.totalResults;
  }

  @computed
  get getTotalExactResults() {
    return this.exactSearchResults.totalResults;
  }

  @computed
  get getSelectedFramework() {
    return this.selectedFramework;
  }

  @computed
  get getCubeTypeOptions() {
    return this.cubeTypeOptions;
  }

  @computed
  get getSelectedCubeTypes() {
    return this.selectedCubeTypes;
  }

  @computed
  get getSelectedDate() {
    return this.selectedDate;
  }

  @computed
  get getAutocompleteResults() {
    return this.autoCompleteResults;
  }

  @computed
  get getAllMaintenanceAgencies() {
    return this.allMaintenanceAgencies;
  }

  getSDDBusinessContent = (type) => {
    switch (type) {
      case "Cube Link Variable":
        return { description: cubeLinkVariables.tooltip };
      case "Reference Framework":
        return { description: referenceFramework.tooltip };
      case "Non-Reference Framework":
        return { description: nonReferenceFramework.tooltip };
      case "Search":
        return { description: searchTooltip.tooltip };
      default:
        return computed(() =>
          this.SDDBusinessContent
            ? this.SDDBusinessContent.find(
                (bc) =>
                  bc.code &&
                  bc.code.replace(/_/g, "").toLowerCase() === type.toLowerCase()
              )
            : {}
        ).get();
    }
  };

  @computed
  get getUserProfiles() {
    return this.userProfiles;
  }

  @computed
  get getSelectedUserProfile() {
    return this.selectedUserProfile;
  }

  @computed
  get getUserProfileChanged() {
    return this.userProfileChanged;
  }

  @computed
  get getSearchFrameworkExpression() {
    return this.searchFrameworkExpression;
  }

  @computed
  get getMacroFrameworksMaintenanceAgencies() {
    return this.macroFrameworksMaintenanceAgencies;
  }

  @action
  setFullProfile = () => {
    this.setSelectedUserProfile(this.userProfiles[2]);
    this.setSelectedCubeTypes(this.userProfiles[2].cubeTypes);
  };

  @action
  fetchAllMaintenanceAgencies = () => {
    if (
      !this.allMaintenanceAgencies ||
      this.allMaintenanceAgencies.length === 0
    ) {
      this.http.service_fetch_ma().then((data) => {
        this.allMaintenanceAgencies = data.results.map((ma) => {
          return {
            name: ma.name,
            maintenanceAgencyId: ma.maintenanceAgencyId,
          };
        });
      });
    }
  };
}
